import { Day, Schedule, TimeRange, DragState, OccupiedBlock } from "./types";
import {
  DAYS,
  DEFAULT_EVENT_START_SLOT,
  TOTAL_SLOTS,
  NIGHT_EVENING_START,
  NIGHT_MORNING_END,
} from "./constants";

// --- Helpers ---

export function timeToSlotIndex(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 2 + (m >= 30 ? 1 : 0);
}

export function slotIndexToTime(index: number): string {
  const h = Math.floor(index / 2);
  const m = index % 2 === 0 ? "00" : "30";
  return `${String(h).padStart(2, "0")}:${m}`;
}

export function getDayEnabledRange(
  day: Day,
  weekdaysEnabled: boolean,
  weekendEnabled: boolean
): { minSlot: number; maxSlot: number } | null {
  if (!weekdaysEnabled && !weekendEnabled) return null;
  if (weekdaysEnabled && weekendEnabled) return { minSlot: 0, maxSlot: TOTAL_SLOTS };

  let min = TOTAL_SLOTS;
  let max = 0;

  if (weekendEnabled) {
    switch (day.key) {
      case "fri": min = Math.min(min, 30); max = Math.max(max, TOTAL_SLOTS); break;
      case "sat": min = Math.min(min, 0); max = Math.max(max, TOTAL_SLOTS); break;
      case "sun": min = Math.min(min, 0); max = Math.max(max, TOTAL_SLOTS); break;
      case "mon": min = Math.min(min, 0); max = Math.max(max, 16); break;
    }
  }

  if (weekdaysEnabled) {
    switch (day.key) {
      case "sun": min = Math.min(min, 42); max = Math.max(max, TOTAL_SLOTS); break;
      case "mon": case "tue": case "wed": case "thu": case "fri":
        min = Math.min(min, 0); max = Math.max(max, TOTAL_SLOTS); break;
      case "sat": min = Math.min(min, 0); max = Math.max(max, 30); break;
    }
  }

  if (min >= max) return null;
  return { minSlot: min, maxSlot: max };
}

export function isDayDisabled(day: Day, weekdaysEnabled: boolean, weekendEnabled: boolean): boolean {
  return getDayEnabledRange(day, weekdaysEnabled, weekendEnabled) === null;
}

// Recorta un rango habilitado de día a la ventana [startTime, endTime].
// Devuelve null si no queda ninguna franja disponible tras el recorte.
export function clampRangeToWindow(
  range: { minSlot: number; maxSlot: number } | null,
  startTime: string,
  endTime: string
): { minSlot: number; maxSlot: number } | null {
  if (!range) return null;
  const minSlot = Math.max(range.minSlot, timeToSlotIndex(startTime));
  const maxSlot = Math.min(range.maxSlot, timeToSlotIndex(endTime));
  if (minSlot >= maxSlot) return null;
  return { minSlot, maxSlot };
}

// Un evento solo es visible si está contenido por completo en [startTime, endTime].
export function isEventVisible(
  event: TimeRange,
  startTime: string,
  endTime: string
): boolean {
  return (
    timeToSlotIndex(event[0]) >= timeToSlotIndex(startTime) &&
    timeToSlotIndex(event[1]) <= timeToSlotIndex(endTime)
  );
}

// Busca el primer hueco libre de 1h (2 slots) alineado a hora para un nuevo evento.
// Prioriza las 08:00; si ahí colisiona, avanza hora a hora y, como último recurso,
// prueba desde el inicio del rango. Devuelve null si el día está ocupado y no cabe nada.
export function findAvailableEventSlot(
  dayEvents: TimeRange[],
  range: { minSlot: number; maxSlot: number }
): TimeRange | null {
  const fits = (s: number, e: number) =>
    !dayEvents.some((r) => {
      const rs = timeToSlotIndex(r[0]);
      const re = timeToSlotIndex(r[1]);
      return s < re && rs < e;
    });
  const preferred = Math.max(range.minSlot, DEFAULT_EVENT_START_SLOT);
  for (const start of [preferred, range.minSlot]) {
    for (let s = start; s < range.maxSlot - 1; s += 2) {
      const e = Math.min(s + 2, range.maxSlot);
      if (fits(s, e)) return [slotIndexToTime(s), slotIndexToTime(e)];
    }
  }
  return null;
}

export function getAvailableCorridor(
  events: TimeRange[],
  excludeIndex: number,
  anchorStart: number,
  anchorEnd: number
): { min: number; max: number } {
  let min = 0;
  let max = TOTAL_SLOTS;
  events.forEach((event, idx) => {
    if (idx === excludeIndex) return;
    const s = timeToSlotIndex(event[0]);
    const e = timeToSlotIndex(event[1]);
    if (e <= anchorStart) min = Math.max(min, e);
    if (s >= anchorEnd) max = Math.min(max, s);
  });
  return { min, max };
}

export function computePreviewEvent(
  drag: DragState,
  dayEvents: TimeRange[],
  range?: { minSlot: number; maxSlot: number } | null
): { start: number; end: number } {
  const excludeIndex = drag.mode === "create" ? -1 : drag.eventIndex;
  const anchorStart = drag.mode === "create" ? drag.initialSlotIndex : drag.originalStart;
  const anchorEnd = drag.mode === "create" ? drag.initialSlotIndex + 1 : drag.originalEnd;
  const corridor = getAvailableCorridor(dayEvents, excludeIndex, anchorStart, anchorEnd);
  const cMin = range ? Math.max(corridor.min, range.minSlot) : corridor.min;
  const cMax = range ? Math.min(corridor.max, range.maxSlot) : corridor.max;

  switch (drag.mode) {
    case "create": {
      const rawMin = Math.min(drag.initialSlotIndex, drag.currentSlotIndex);
      const rawMax = Math.max(drag.initialSlotIndex, drag.currentSlotIndex);
      return { start: Math.max(cMin, rawMin), end: Math.min(cMax, rawMax + 1) };
    }
    case "move": {
      const duration = drag.originalEnd - drag.originalStart;
      const offset = drag.initialSlotIndex - drag.originalStart;
      let newStart = drag.currentSlotIndex - offset;
      newStart = Math.max(cMin, Math.min(cMax - duration, newStart));
      return { start: newStart, end: newStart + duration };
    }
    case "resize-top": {
      const newStart = Math.max(cMin, Math.min(drag.originalEnd - 1, drag.currentSlotIndex));
      return { start: newStart, end: drag.originalEnd };
    }
    case "resize-bottom": {
      const newEnd = Math.min(cMax, Math.max(drag.originalStart + 1, drag.currentSlotIndex + 1));
      return { start: drag.originalStart, end: newEnd };
    }
  }
}

export function canCopyAllEventsToDay(
  sourceEvents: TimeRange[],
  targetRange: { minSlot: number; maxSlot: number },
  existingEvents: TimeRange[]
): boolean {
  let hasFittingEvent = false;
  for (const event of sourceEvents) {
    const s = Math.max(timeToSlotIndex(event[0]), targetRange.minSlot);
    const e = Math.min(timeToSlotIndex(event[1]), targetRange.maxSlot);
    if (s >= e) continue;
    hasFittingEvent = true;
    const collides = existingEvents.some((r) => {
      const rs = timeToSlotIndex(r[0]);
      const re = timeToSlotIndex(r[1]);
      return s < re && rs < e;
    });
    if (collides) return false;
  }
  return hasFittingEvent;
}

export function copyEventsToDay(
  sourceEvents: TimeRange[],
  targetRange: { minSlot: number; maxSlot: number }
): TimeRange[] {
  const result: TimeRange[] = [];
  for (const event of sourceEvents) {
    const s = Math.max(timeToSlotIndex(event[0]), targetRange.minSlot);
    const e = Math.min(timeToSlotIndex(event[1]), targetRange.maxSlot);
    if (s >= e) continue;
    result.push([slotIndexToTime(s), slotIndexToTime(e)]);
  }
  return result;
}

// --- Occupied helpers ---

// Rangos ocupados de un día por los contratos de la candidata, ordenados por hora de inicio.
export function getOccupiedRangesForDay(
  blocks: OccupiedBlock[] | undefined,
  dayKey: string
): { block: OccupiedBlock; range: TimeRange }[] {
  const result: { block: OccupiedBlock; range: TimeRange }[] = [];
  (blocks || []).forEach((block) => {
    (block.schedule[dayKey] || []).forEach((range) => {
      result.push({ block, range });
    });
  });
  return result.sort(
    (a, b) => timeToSlotIndex(a.range[0]) - timeToSlotIndex(b.range[0])
  );
}

// Texto del bloque ocupado: «{contrato} · {cliente}», omitiendo lo que falte.
export function formatOccupiedBlockLabel(
  block: Pick<OccupiedBlock, "contractName" | "clientName">
): string {
  return [block.contractName, block.clientName]
    .filter((part): part is string => !!part)
    .join(" · ");
}

// --- Night helpers ---

const toSlotRange = ([start, end]: TimeRange): [number, number] => [
  timeToSlotIndex(start),
  timeToSlotIndex(end),
];

// Fusiona los rangos de slots que se solapan; los adyacentes se mantienen separados.
function mergeOverlappingSlotRanges(
  ranges: [number, number][]
): [number, number][] {
  const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  sorted.forEach(([s, e]) => {
    const last = merged[merged.length - 1];
    if (last && s < last[1]) {
      last[1] = Math.max(last[1], e);
    } else {
      merged.push([s, e]);
    }
  });
  return merged;
}

// Cada noche que arranca en `startDayKeys` ocupa 21:00-24:00 ese día y 00:00-09:00 del día siguiente.
// Los segmentos se recortan al rango habilitado de cada día (los días deshabilitados se omiten).
// Si un segmento se solapa con un evento existente, se fusionan en un único evento.
export function applyNightSchedule(
  value: Schedule,
  startDayKeys: string[],
  weekdaysEnabled: boolean,
  weekendEnabled: boolean
): Schedule {
  const segments: Record<string, [number, number][]> = {};
  const addSegment = (key: string, start: number, end: number) => {
    (segments[key] ||= []).push([start, end]);
  };
  startDayKeys.forEach((key) => {
    const idx = DAYS.findIndex((d) => d.key === key);
    const nextKey = DAYS[(idx + 1) % DAYS.length].key;
    addSegment(key, NIGHT_EVENING_START, TOTAL_SLOTS);
    addSegment(nextKey, 0, NIGHT_MORNING_END);
  });

  const result: Schedule = { ...value };
  DAYS.forEach((day) => {
    const segs = segments[day.key];
    if (!segs) return;
    const range = getDayEnabledRange(day, weekdaysEnabled, weekendEnabled);
    if (!range) return;
    const dayRanges = (result[day.key] || []).map(toSlotRange);
    segs.forEach(([s, e]) => {
      const cs = Math.max(s, range.minSlot);
      const ce = Math.min(e, range.maxSlot);
      if (cs >= ce) return;
      dayRanges.push([cs, ce]);
    });
    if (!dayRanges.length) return;
    result[day.key] = mergeOverlappingSlotRanges(dayRanges).map(
      ([s, e]): TimeRange => [slotIndexToTime(s), slotIndexToTime(e)]
    );
  });
  return result;
}

const nextDayKey = (key: string): string => {
  const idx = DAYS.findIndex((d) => d.key === key);
  return DAYS[(idx + 1) % DAYS.length].key;
};

// Comprueba si el horario cubre todas las franjas nocturnas (21:00-09:00)
// de las noches que arrancan en `startDayKeys`. La cobertura puede estar
// repartida entre varios eventos contiguos o solapados.
export function hasNightSchedule(
  value: Schedule,
  startDayKeys: string[]
): boolean {
  const covers = (dayKey: string, start: number, end: number) => {
    const ranges = (value[dayKey] || [])
      .map(toSlotRange)
      .sort((a, b) => a[0] - b[0]);
    let covered = start;
    ranges.forEach(([s, e]) => {
      if (s <= covered && e > covered) covered = e;
    });
    return covered >= end;
  };
  return startDayKeys.every(
    (key) =>
      covers(key, NIGHT_EVENING_START, TOTAL_SLOTS) &&
      covers(nextDayKey(key), 0, NIGHT_MORNING_END)
  );
}

// Elimina la disponibilidad nocturna (21:00-24:00 y 00:00-09:00 del día siguiente)
// de las noches que arrancan en `startDayKeys`. Los eventos que entran
// parcialmente en la franja nocturna se recortan a su parte diurna.
export function clearNightSchedule(
  value: Schedule,
  startDayKeys: string[]
): Schedule {
  const eveningKeys = new Set(startDayKeys);
  const morningKeys = new Set(startDayKeys.map(nextDayKey));
  const result: Schedule = {};
  DAYS.forEach((day) => {
    const events = value[day.key];
    if (!events?.length) return;
    const kept = events
      .map((event): TimeRange | null => {
        let [s, e] = toSlotRange(event);
        if (morningKeys.has(day.key)) s = Math.max(s, NIGHT_MORNING_END);
        if (eveningKeys.has(day.key)) e = Math.min(e, NIGHT_EVENING_START);
        if (s >= e) return null;
        return [slotIndexToTime(s), slotIndexToTime(e)];
      })
      .filter((r): r is TimeRange => r !== null);
    if (kept.length) result[day.key] = kept;
  });
  return result;
}

// Elimina los eventos cuya hora de inicio y fin queden dentro de la franja nocturna (21:00-09:00).
export function clearNightEvents(value: Schedule): Schedule {
  return clearNightSchedule(
    value,
    DAYS.map((d) => d.key)
  );
}
